import type { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { flatMap } from "lodash"
import type { StateObject } from "../../redux/types"
import { getCurrentPathQuery, getRecordEditId } from "../../router/selectors"
import {
    isItemsAndCategoriesAgendaOpened,
    isLocationsAndTerminalsAgendaOpened,
    isSchematizedAgendaOpened,
} from "../../data/selectors"
import { getSettingsByKey, isDeliverySettingsAgendaOpened, isMibSettingsAgendaOpened } from "../settings/selectors"
import type { TerminalState } from "./reducer"
import {
    AggregatorConfig,
    FoodmeowStore,
    Location,
    LocationDetail,
    OAuthTokens,
    ServiceDetail,
    Terminal,
    UberStore,
} from "../../../graphql/types"
import { TerminalType } from "../../../graphql/types"
import type { AggregatorFormItem } from "./aggregatorForm/components"
import { ServiceMetadata, ServiceOnlineOrderingStatus } from "./aggregatorForm/components"
import type { AggregatorType } from "./aggregatorForm/aggregatorTypeEnum"
import { AggregatorTypeEnum } from "./aggregatorForm/aggregatorTypeEnum"
import { MultiSelectItem } from "../../uiKit/MultiSelect/MultiSelect"
import { flatten, isNil, map, reject } from "ramda"
import { getLocationsAuthData } from "../../auth/selectors"
import { getPriceMultiplierValue } from "./utils"
import { pipeline } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { getAllAssignments } from "../assignments/selectors"
import { LocationsTableItem, LocationsTableServiceItem } from "./types"
import { shouldDownloadLocationsForGoogleReviews } from "../reports/customers/customersTable/selectors"

export const getTerminalStateObject = (state: StateObject): TerminalState => state.terminal

export const getLocations = (state: StateObject): Location[] => getTerminalStateObject(state).data || []

export const getLocationsListData = (state: StateObject): Maybe<LocationsTableItem[]> => {
    const data = getTerminalStateObject(state).locationsList
    const settings = getSettingsByKey(state, "MIB_ENABLED_FOR_TERMINAL")

    if (!data) {
        return null
    }

    return data.map(location => ({
        ...location,
        children: location.children.map(service => ({
            ...service,
            isAcceptingOnlineOrderingOrders: !!settings.find(
                s => s.terminal_id === service.id && s.key === "MIB_ENABLED_FOR_TERMINAL" && s.value === "true"
            ),
        })),
    }))
}

export const getTerminals = (state: StateObject): Terminal[] =>
    flatten(getLocations(state).map(l => l.terminals)) as unknown as Terminal[]

export const shouldDownloadLocationsTerminalsData = (state: StateObject): boolean =>
    isLocationsAndTerminalsAgendaOpened(state) ||
    isMibSettingsAgendaOpened(state) ||
    isDeliverySettingsAgendaOpened(state) ||
    isSchematizedAgendaOpened(state) ||
    isItemsAndCategoriesAgendaOpened(state) ||
    shouldDownloadLocationsForGoogleReviews(state)

export const getEditLocationId = (state: StateObject) => getRecordEditId(state)

export const getLocationById = (state: StateObject, id: string) => getLocations(state).find(l => l.id === id)

export const getLocationDetailData = (state: StateObject): Maybe<LocationDetail> =>
    getTerminalStateObject(state).locationDetail

export const getGoogleBusinessInfo = (state: StateObject) => getTerminalStateObject(state).googleBusinessInfo

export const getGoogleBusinessInfoError = (state: StateObject) => getTerminalStateObject(state).googleBusinessInfoError

export const getGoogleBusinessIntegrationError = (state: StateObject) =>
    getTerminalStateObject(state).googleBusinessIntegrationError

export const getIsGoogleBusinessInfoLoading = (state: StateObject) => {
    const terminalState = getTerminalStateObject(state)
    return (
        terminalState.googleBusinessInfoRequested &&
        !terminalState.googleBusinessInfo &&
        !terminalState.googleBusinessInfoError
    )
}

export const getIsSubmittingGoogleBusinessIntegration = (state: StateObject) =>
    getTerminalStateObject(state).isSubmittingGoogleBusinessIntegration

export const getShowGoogleLocationPicker = (state: StateObject) => {
    const terminalState = getTerminalStateObject(state)
    return (
        terminalState.googleBusinessInfoRequested ||
        !!terminalState.googleBusinessInfo ||
        !!terminalState.googleBusinessInfoError ||
        terminalState.isSubmittingGoogleBusinessIntegration ||
        !!terminalState.googleBusinessIntegrationError
    )
}

export const getIsRemovingGoogleIntegration = (state: StateObject) => {
    const terminalState = getTerminalStateObject(state)
    return terminalState.removeGoogleIntegrationRequested && !terminalState.removeGoogleIntegrationError
}

export const getRemoveGoogleIntegrationError = (state: StateObject) =>
    getTerminalStateObject(state).removeGoogleIntegrationError

export const getEditTerminalId = (state: StateObject) => getRecordEditId(state)

export const getServiceDetailData = (state: StateObject): Maybe<ServiceDetail> =>
    getTerminalStateObject(state).serviceDetail as ServiceDetail

export const getServiceListItemById = (state: StateObject, id: Maybe<string>): Maybe<LocationsTableServiceItem> =>
    flatMap(getLocationsListData(state), l => l.children).find(s => s.id === id)

export const getEditServiceListItem = (state: StateObject): Maybe<LocationsTableServiceItem> =>
    getServiceListItemById(state, getRecordEditId(state))

export const getIsLoadingServiceDetail = (state: StateObject) => getTerminalStateObject(state).loadingServiceDetail

export const getAggregatorFormData = (state: StateObject): Maybe<AggregatorFormItem> => {
    const service = getServiceDetailData(state)
    const locations = getLocationsAuthData(state)

    if (!service) {
        return null
    }

    const location = locations.find(l => l.id === service.locationId)
    const locationName = location ? location.name : ""

    const storeConfig = service.aggregatorConfig && service.aggregatorConfig.figure

    return {
        terminalId: service.id,
        name: service.name,
        isActive: !!service.isActive,
        aggregatorType: getAggregatorType(service.aggregatorType, service.aggregatorConfig),
        location: {
            id: service.locationId,
            name: locationName,
        },
        // @ts-expect-error
        url: service.url,
        description: storeConfig && storeConfig.description,
        storeLogo: storeConfig && storeConfig.logo,
        storeImages: storeConfig && storeConfig.images,
        metadata: service.metadata
            ? ({
                  ...service.metadata,
                  onlineOrdering: {
                      ...service.metadata?.onlineOrdering,
                      priceMultiplier: getPriceMultiplierValue(service.metadata?.onlineOrdering?.priceMultiplier),
                  },
              } as Maybe<ServiceMetadata>)
            : null,
    }
}

export const getAggregatorType = (type: Maybe<string>, config: Maybe<AggregatorConfig>): Maybe<AggregatorType> => {
    if (config) {
        if (config.uber) {
            return AggregatorTypeEnum.Uber
        }
        if (config.email) {
            return AggregatorTypeEnum.Email
        }
        if (config.figure) {
            return AggregatorTypeEnum.Foodmeow
        }
    }

    switch (type) {
        case "uber":
            return AggregatorTypeEnum.Uber
        case "email":
            return AggregatorTypeEnum.Email
        case "figure":
            return AggregatorTypeEnum.Foodmeow
        case "grubhub":
            return AggregatorTypeEnum.Grubhub
        case "slice":
            return AggregatorTypeEnum.Slice
        case "westsideeats":
            return AggregatorTypeEnum.WestSideEats
        case "doordash":
            return AggregatorTypeEnum.DoorDash
    }

    return null
}

export const getAggregatorIntegrationData = (state: StateObject): Maybe<AggregatorConfig> => {
    const service = getServiceDetailData(state)

    if (!service) {
        return null
    }

    return service.aggregatorConfig
}

export const isAggregatorIntegrated = (state: StateObject, strict: boolean = false): boolean => {
    if (getAggregatorIntegrationData(state)) {
        return true
    }
    const aggregatorFormData = getAggregatorFormData(state)
    switch (aggregatorFormData?.aggregatorType) {
        case AggregatorTypeEnum.Grubhub:
            return !!aggregatorFormData?.metadata?.onlineOrdering
        case AggregatorTypeEnum.Uber:
            if (strict) {
                return aggregatorFormData?.metadata?.onlineOrdering?.status
                    ? aggregatorFormData?.metadata?.onlineOrdering?.status !== ServiceOnlineOrderingStatus.Pending
                    : false
            }
            return true
        default:
            return false
    }
}

export const getIsLoadingAggregatorForm = (state: StateObject): boolean => {
    return state.terminal.aggregatorForm.loading
}

export const getAggregatorFormError = (state: StateObject): Maybe<Error> => {
    return state.terminal.aggregatorForm.error
}

export const getAggregatorUberStores = (state: StateObject): UberStore[] | null => {
    if (!state.terminal.aggregatorForm.uber) {
        return null
    }

    return state.terminal.aggregatorForm.uber.stores
}

export const getAggregatorFoodmeowStores = (state: StateObject): FoodmeowStore[] | null => {
    if (!state.terminal.aggregatorForm.foodmeow) {
        return null
    }

    return state.terminal.aggregatorForm.foodmeow.stores
}

export const getAggregatorFoodmeowAuthToken = (state: StateObject): Maybe<string> => {
    if (!state.terminal.aggregatorForm.foodmeow) {
        return null
    }

    return state.terminal.aggregatorForm.foodmeow.token
}

export const getAggregatorUberAuth = (state: StateObject): Maybe<OAuthTokens> => {
    if (!state.terminal.aggregatorForm.uber) {
        return null
    }

    return state.terminal.aggregatorForm.uber.uberAuth
}

export const getKioskImagesFormOpened = (state: StateObject): boolean => {
    const query = getCurrentPathQuery(state)
    return !!(query && query.screen)
}

export const getDataForKioskCopyImagesTerminalPicker = (state: StateObject): MultiSelectItem[] => {
    const locations = getLocationsAuthData(state)
    const editedTerminalId = getRecordEditId(state)

    if (!editedTerminalId) {
        return []
    }

    return pipeline(
        locations,
        map(location => {
            const terminals = location.terminals.filter(
                terminal => terminal.type === TerminalType.Kiosk && terminal.id !== editedTerminalId
            )

            if (terminals.length === 0) {
                return null
            }

            return {
                id: location.id,
                label: location.name,
                labelOnly: true,
                children: terminals.map(terminal => ({
                    id: terminal.id,
                    label: terminal.name,
                })),
            }
        }),
        reject(isNil)
    ) as MultiSelectItem[]
}

export const getDataForOsbAssignmentsPicker = (state: StateObject, locationId: string): MultiSelectItem[] => {
    const assignments = getAllAssignments(state).filter(a => a.locationId === locationId)

    return pipeline(
        assignments,
        map(section => {
            const assignments = section.children || []
            if (assignments.length === 0) {
                return null
            }
            return {
                id: section.id,
                label: section.name,
                children: assignments.map(assignment => ({
                    id: assignment.id,
                    label: assignment.name,
                })),
            }
        }),
        reject(isNil)
    ) as MultiSelectItem[]
}
